import { useEffect, useRef, useState } from "react";
import { LoaderCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  formatBillingOfferPrice,
  getBillingOffer,
} from "@/features/billing/billingOffers";
import { BillingManagementCard } from "@/features/billing/components/BillingManagementCard";
import { BillingPlanGrid } from "@/features/billing/components/BillingPlanGrid";
import { CreditBalanceCard } from "@/features/billing/components/CreditBalanceCard";
import { CreditHistoryList } from "@/features/billing/components/CreditHistoryList";
import { TopUpPanel } from "@/features/billing/components/TopUpPanel";
import { useBilling } from "@/features/billing/hooks/useBilling";
import type { BillingInterval } from "@/features/billing/billing.types";
import { subscriptionStore } from "@/stores/subscriptionStore";

export function BillingPage() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { billingState, checkout, hydrateBilling, openBillingPortal } = useBilling();
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const handledBillingStateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!billingState?.currentPlanOfferCode) {
      return;
    }

    setInterval(
      billingState.currentPlanOfferCode.endsWith("_annual")
        ? "annual"
        : "monthly",
    );
  }, [billingState?.currentPlanOfferCode]);

  useEffect(() => {
    const billingReturnState = new URLSearchParams(location.search).get("billing");

    if (!billingReturnState) {
      return;
    }

    if (handledBillingStateRef.current === billingReturnState) {
      return;
    }

    handledBillingStateRef.current = billingReturnState;

    const params = new URLSearchParams(location.search);
    params.delete("billing");
    const normalizedSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: normalizedSearch ? `?${normalizedSearch}` : "",
      },
      { replace: true },
    );

    if (billingReturnState !== "success") {
      return;
    }

    let cancelled = false;
    const baseline = {
      balance: billingState?.wallet.balance ?? null,
      historyHeadId: billingState?.history[0]?.id ?? null,
      tier: billingState?.currentTier ?? null,
    };

    const refreshAfterReturn = async () => {
      const maxAttempts = 24;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) {
          return;
        }

        await hydrateBilling();

        const latestState = subscriptionStore.getState().billingState;
        const hasSettledUpdate = latestState
          ? (
            latestState.wallet.balance !== baseline.balance
            || (latestState.history[0]?.id ?? null) !== baseline.historyHeadId
            || latestState.currentTier !== baseline.tier
          )
          : false;

        if (hasSettledUpdate) {
          return;
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }
    };

    void refreshAfterReturn();

    return () => {
      cancelled = true;
    };
  }, [billingState, hydrateBilling, location.pathname, location.search, navigate]);

  const closeBillingView = () => {
    navigate("/app", { replace: true });
  };

  if (!billingState) {
    return (
      <section className="mx-auto max-w-[92rem] overflow-hidden rounded-[2rem] border border-[#e1d7ca] bg-[#fffdf9] shadow-[0_28px_80px_-42px_rgba(34,26,19,0.38)]">
        <div className="flex items-center justify-between border-b border-[#ece2d6] px-6 py-7 md:px-10">
          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
              {t("billing.eyebrow")}
            </p>
            <h1 className="font-display text-[clamp(2.4rem,4vw,3.5rem)] leading-[0.94] text-ink">
              {t("billing.title")}
            </h1>
          </div>
          <button
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="inline-flex size-12 items-center justify-center rounded-full text-ink/55 transition hover:bg-[#f5efe7] hover:text-ink"
            onClick={closeBillingView}
            type="button"
          >
            <X className="size-7" />
          </button>
        </div>
        <div className="px-6 py-10 text-sm text-ink/70 md:px-10">
          <div className="flex items-center gap-3">
            <LoaderCircle className="size-4 animate-spin" />
            {t("billing.loading")}
          </div>
        </div>
      </section>
    );
  }

  const currentOffer = billingState.currentPlanOfferCode
    ? getBillingOffer(billingState.currentPlanOfferCode, billingState.offers)
    : null;
  const currentOfferPrice = currentOffer
    ? formatBillingOfferPrice(currentOffer, {
        intervalLabel:
          currentOffer.kind === "subscription"
            ? t(`billing.interval.${currentOffer.billingInterval}`)
            : null,
        locale: i18n.resolvedLanguage ?? i18n.language,
      })
    : null;

  return (
    <section className="mx-auto max-w-[92rem] overflow-hidden rounded-[2rem] border border-[#e1d7ca] bg-[#fffdf9] shadow-[0_32px_90px_-46px_rgba(34,26,19,0.42)]">
      <header className="flex items-start justify-between gap-6 border-b border-[#ece2d6] px-6 py-7 md:px-10 md:py-8">
        <div className="max-w-4xl space-y-3">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
            {t("billing.eyebrow")}
          </p>
          <div className="space-y-2">
            <h1 className="font-display text-[clamp(2.5rem,4.4vw,3.7rem)] leading-[0.94] text-ink">
              {t("billing.title")}
            </h1>
            <p className="max-w-3xl text-base leading-7 text-ink/65">{t("billing.subtitle")}</p>
          </div>
        </div>

        <button
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-full text-ink/55 transition hover:bg-[#f5efe7] hover:text-ink"
          onClick={closeBillingView}
          type="button"
        >
          <X className="size-7" />
        </button>
      </header>

      <div className="max-h-[calc(100vh-11.5rem)] overflow-y-auto">
        <div className="divide-y divide-[#ece2d6]">
          <section className="px-6 py-8 md:px-10 md:py-9">
            <BillingPlanGrid
              currentPlanOfferCode={billingState.currentPlanOfferCode}
              interval={interval}
              onCheckout={(offerCode) => {
                void checkout(offerCode);
              }}
              onIntervalChange={setInterval}
              offers={billingState.offers}
            />
          </section>

          <section className="px-6 py-8 md:px-10 md:py-9">
            <CreditBalanceCard wallet={billingState.wallet} />
          </section>

          <section className="px-6 py-8 md:px-10 md:py-9">
            <TopUpPanel
              canTopUp={billingState.canTopUp}
              onCheckout={(offerCode) => {
                void checkout(offerCode);
              }}
              offers={billingState.offers}
            />
          </section>

          <section className="px-6 py-8 md:px-10 md:py-9">
            <BillingManagementCard
              billingState={billingState}
              onOpenPortal={() => {
                void openBillingPortal({ returnPath: "/account/billing" });
              }}
            />
          </section>

          <section className="px-6 py-8 md:px-10 md:py-9">
            <CreditHistoryList
              currentBalance={billingState.wallet.balance}
              history={billingState.history}
            />
          </section>
        </div>
      </div>

      <footer className="flex flex-col gap-4 border-t border-[#ece2d6] px-6 py-6 md:flex-row md:items-center md:justify-between md:px-10">
        <p className="text-sm text-ink/58">
          {billingState.nextBillingDate && currentOfferPrice
            ? t("billing.footer.nextInvoice", {
                defaultValue: `Your next invoice will be on ${billingState.nextBillingDate} for ${currentOfferPrice}.`,
                date: billingState.nextBillingDate,
                price: currentOfferPrice,
              })
            : t("billing.footer.freePlan", {
                defaultValue: "Upgrade anytime to unlock recurring billing and credit top-ups.",
              })}
        </p>

        <Button
          className="self-start px-0 text-lg font-medium text-ink/72 shadow-none hover:bg-transparent hover:text-ink md:self-auto"
          onClick={closeBillingView}
          type="button"
          variant="ghost"
        >
          {t("common.close", { defaultValue: "Close" })}
        </Button>
      </footer>
    </section>
  );
}
